import * as React from 'react';

import DocumentCreationButton from 'Components/DocumentCreationButton';
import DocumentHeader from '../../../components/DocumentHeader';
import DocumentWrapper from '../../../components/DocumentWrapper';
import FormEditingHandler from '../../../containers/FormEditingHandler';

import TDCSForm from './Form';
import TDCSHistory from './HistoryRowData';
import { useIntl } from 'react-intl';
import { sortPartialDate } from 'neuro-utils';
import DoctorsOrdersReview from './Form/Sessions/components/DoctorsOrdersReview';
import { useAppSelector } from 'Store/index';
import { filterByDocumentType } from 'Utility/documentHandling';

export const TDCSContext = React.createContext<ITDCSContext>({
  fm: (m: string) => m,
  isEditing: false,
  formData: {
    document: {},
    onChange: () => {
      return;
    },
  },
  startEdit: (d: { _id?: string }) => (e: React.MouseEvent<Element, MouseEvent> | undefined) => {
    !!d;
    !!e;
    return;
  },
  documents: [],
  doctorsOrders: [],
  otherDoctorsOrders: [],
  ninmtTreatmentPeriods: [],
  contraIndications: [],
  symptomsAndLocations: [],
  doctorsOrdersReview: { active: false, completed: false },
  setDoctorsOrdersReview: () => '',
  anchor: undefined,
  setAnchor: () => '',
  /** For treatment efficiency functionalities */
  patientMySQStatus: undefined,
  /** For treatment efficiency functionalities */
  taskLists: [],
  /** For treatment efficiency functionalities */
  sortedAndMergedMyDocuments: [],
});

interface ITDCSContext {
  fm: (m: string) => string;
  isEditing: boolean;
  formData: IFormData<IDocumentWithTypeV2<'tdcs'>>;
  documents: IDocumentWithTypeV2<'tdcs'>[];
  doctorsOrders: IDocumentWithTypeV2<'doctorsOrders'>[];
  otherDoctorsOrders: IDocumentWithTypeV2<'doctorsOrders'>[];
  ninmtTreatmentPeriods: IDocumentWithTypeV2<'ninmtTreatmentPeriod'>[];
  contraIndications: IDocumentWithTypeV2<'contraIndicationsToTreatment'>[];
  symptomsAndLocations: IDocumentWithTypeV2<'symptomsAndLocations'>[];
  doctorsOrdersReview: { active: boolean; completed: boolean };
  setDoctorsOrdersReview: React.Dispatch<
    React.SetStateAction<{
      active: boolean;
      completed: boolean;
    }>
  >;
  anchor: string | undefined;
  setAnchor: React.Dispatch<React.SetStateAction<string | undefined>>;
  startEdit: StartEdit;
  patientMySQStatus: string | null | undefined;
  taskLists: ITaskList[];
  sortedAndMergedMyDocuments: IDocumentV2[];
}

const TDCSContent = ({
  editing,
  startEdit,
  formData,
  view,
  fm,
  documents,
  doctorsOrders,
  otherDoctorsOrders,
  ninmtTreatmentPeriods,
  contraIndications,
  symptomsAndLocations,
  doctorsOrdersReview,
  setDoctorsOrdersReview,
  anchor,
  setAnchor,
  patientMySQStatus,
  taskLists,
  sortedAndMergedMyDocuments,
  latestOrderWithTDCS,
}: ITDCSContentProps): React.JSX.Element => {
  const tdcsContext: ITDCSContext = React.useMemo(
    () => ({
      fm,
      isEditing: !!editing && !view?.viewing,
      formData,
      documents,
      doctorsOrders,
      otherDoctorsOrders,
      ninmtTreatmentPeriods,
      contraIndications,
      symptomsAndLocations,
      doctorsOrdersReview,
      setDoctorsOrdersReview,
      anchor,
      setAnchor,
      startEdit,
      patientMySQStatus,
      taskLists,
      sortedAndMergedMyDocuments,
    }),
    [
      fm,
      editing,
      view,
      formData,
      documents,
      doctorsOrders,
      otherDoctorsOrders,
      ninmtTreatmentPeriods,
      contraIndications,
      symptomsAndLocations,
      doctorsOrdersReview,
      setDoctorsOrdersReview,
      anchor,
      setAnchor,
      startEdit,
      patientMySQStatus,
      taskLists,
      sortedAndMergedMyDocuments,
    ],
  );

  return (
    <TDCSContext.Provider value={tdcsContext}>
      <DocumentWrapper>
        <DocumentHeader
          name="tdcs"
          headerId={doctorsOrdersReview.active ? 'tdcs.doctorsOrdersReview' : 'tdcs.title'}
          headerWidth={9}
          infoText={doctorsOrdersReview.active ? 'tdcs.uncompletedDoctorsOrderMessage' : undefined}
          editing={editing}
          editButtons={
            <DocumentCreationButton
              name="tdcs"
              text="tdcs.newTdcs"
              onClick={(e: React.MouseEvent): void => {
                startEdit({}, 'tdcs')(e);
              }}
              disabled={documents.some(d => !d.hasEnded) || Boolean(latestOrderWithTDCS?.tdcs?.endTDCS)}
              disabledTooltip={
                latestOrderWithTDCS?.tdcs?.endTDCS ? (
                  <span>{fm('tdcs.unableToAddNew')}</span>
                ) : (
                  <span>{fm('tdcs.endOngoingToAddNew')}</span>
                )
              }
            />
          }
        />
        {editing ? (
          <React.Fragment>
            <div style={{ display: doctorsOrdersReview.active ? 'none' : undefined }}>
              <TDCSForm />
            </div>
            <div style={{ display: doctorsOrdersReview.active ? undefined : 'none' }}>
              <DoctorsOrdersReview />
            </div>
          </React.Fragment>
        ) : null}
        {!editing && !view?.viewing ? <TDCSHistory /> : null}
      </DocumentWrapper>
    </TDCSContext.Provider>
  );
};

const TDCS = ({ documents }: IOwnProps): React.JSX.Element => {
  const patientMySQStatus = useAppSelector((s: IState) => s.myapp.mysqUserId);
  const taskLists = useAppSelector((s: IState) => s.myapp.taskLists);
  const sortedAndMergedMyDocs = useAppSelector((s: IState) => s.myapp.sortedAndMergedDocuments);

  const [doctorsOrdersReview, setDoctorsOrdersReview] = React.useState<{ active: boolean; completed: boolean }>({
    active: false,
    completed: false,
  });

  // Anchor for scrolling to specific sections in form
  const [anchor, setAnchor] = React.useState<string | undefined>(undefined);

  const { formatMessage } = useIntl();
  const fm = (id: string) => formatMessage({ id });
  const ninmtTreatmentPeriodDocs = documents.filter(
    d => d._type === 'ninmtTreatmentPeriod',
  ) as IDocumentWithTypeV2<'ninmtTreatmentPeriod'>[];

  let doctorsOrdersDocs: IDocumentWithTypeV2<'doctorsOrders'>[] = [];
  let otherDoctorsOrdersDocs: IDocumentWithTypeV2<'doctorsOrders'>[] = [];
  let contraIndicationsDocs: IDocumentWithTypeV2<'contraIndicationsToTreatment'>[] = [];
  let symptomsAndLocationsDocs: IDocumentWithTypeV2<'symptomsAndLocations'>[] = [];
  let latestOrderWithTDCS: IDocumentWithTypeV2<'doctorsOrders'> | undefined;

  if (Array.isArray(ninmtTreatmentPeriodDocs) && ninmtTreatmentPeriodDocs.length > 0) {
    const latestTreatmentPeriodDate = ninmtTreatmentPeriodDocs.sort((n1, n2) => sortPartialDate(n2.date, n1.date))[0]
      ?.date;

    doctorsOrdersDocs = filterByDocumentType(documents, 'doctorsOrders').filter(
      d =>
        // Only doctor's orders with TDCS are relevant
        Array.isArray(d.treatmentType) &&
        d.treatmentType.includes('tdcs') &&
        sortPartialDate(d.date, latestTreatmentPeriodDate) >= 0,
    );
    latestOrderWithTDCS = doctorsOrdersDocs.toSorted((d1, d2) => sortPartialDate(d2.date, d1.date))[0];

    otherDoctorsOrdersDocs = filterByDocumentType(documents, 'doctorsOrders').filter(
      d =>
        sortPartialDate(d.date, latestTreatmentPeriodDate) >= 0 &&
        (!Array.isArray(d.treatmentType) || d.treatmentType.length === 0) &&
        Object.keys(d).some(k => ['nextInterview', 'ninmtMeeting', 'requestFollowUp'].includes(k)),
    );

    contraIndicationsDocs = filterByDocumentType(documents, 'contraIndicationsToTreatment').filter(
      d => sortPartialDate(d.date, latestTreatmentPeriodDate) >= 0,
    );

    symptomsAndLocationsDocs = filterByDocumentType(documents, 'symptomsAndLocations').filter(
      d => sortPartialDate(d.startDate, latestTreatmentPeriodDate) >= 0,
    );
  }

  const parsedDocs = structuredClone(documents);
  const tdcsDocs = filterByDocumentType(parsedDocs, 'tdcs').map(d => {
    if (Array.isArray(d.sessions) && d.sessions.length > 0) {
      d.sessions = d.sessions.filter(s => !(s.removeTS ?? s.removeInfo));
    }
    return d;
  });

  // Track editing status of documents
  const isEditing = tdcsDocs.some(d => d._editing);

  // If not editing any docs, reset doctorsOrdersReview status if active
  React.useEffect(() => {
    if (!isEditing && doctorsOrdersReview.active) {
      setDoctorsOrdersReview({ active: false, completed: false });
    }
  }, [isEditing]);

  // Scroll to top when starting review of doctor's orders
  React.useEffect(() => {
    if (doctorsOrdersReview.active) {
      scrollTo(0, 0);
    } else {
      // Reset anchor when review is done
      setTimeout(() => setAnchor(undefined), 600);
    }
  }, [doctorsOrdersReview.active]);

  const contentProps: Omit<ITDCSContentProps, 'editing' | 'startEdit' | 'formData' | 'view'> = {
    fm,
    documents: tdcsDocs,
    doctorsOrders: doctorsOrdersDocs,
    otherDoctorsOrders: otherDoctorsOrdersDocs,
    ninmtTreatmentPeriods: ninmtTreatmentPeriodDocs,
    contraIndications: contraIndicationsDocs,
    symptomsAndLocations: symptomsAndLocationsDocs,
    doctorsOrdersReview,
    setDoctorsOrdersReview,
    anchor,
    setAnchor,
    patientMySQStatus,
    taskLists,
    sortedAndMergedMyDocuments: sortedAndMergedMyDocs,
    latestOrderWithTDCS,
  };

  return (
    <FormEditingHandler
      name="tdcs"
      documents={tdcsDocs}
      toolbarProps={{
        saveButtonDisabled: doctorsOrdersReview.active,
        cancelButtonDisabled: doctorsOrdersReview.active,
      }}
    >
      {(editing, startEdit, formData, view): React.JSX.Element => (
        <TDCSContent editing={editing} startEdit={startEdit} formData={formData} view={view} {...contentProps} />
      )}
    </FormEditingHandler>
  );
};

interface ITDCSContentProps extends Omit<ITDCSContext, 'isEditing'> {
  editing: string;
  view?: IView;
  latestOrderWithTDCS: IDocumentWithTypeV2<'doctorsOrders'> | undefined;
}

interface IOwnProps {
  documents: IDocumentWithTypeV2<
    'tdcs' | 'doctorsOrders' | 'ninmtTreatmentPeriod' | 'contraIndicationsToTreatment' | 'symptomsAndLocations'
  >[];
}

export default TDCS;
